import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import InputMonto from '../../components/InputMonto';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import {
    getTramiteGananciasById, actualizarChecklistItem, actualizarTramiteGanancias,
    actualizarAnalisisGanancias, actualizarComentariosGanancias, avanzarEstadoGanancias,
} from '../../services/gananciasService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';
import { apellidoNombre } from '../../utils/nombrePersona';
import EtiquetaTramite from '../../components/EtiquetaTramite';

// Clase del badge contador (X/Y): verde cuando está completo, gris en caso contrario.
const claseBadgeContador = (completados, total) => (total > 0 && completados === total ? 'bg-success' : 'bg-secondary');

const formatMoneda = (valor) => (valor == null ? null : Number(valor).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }));

const BENEFICIO_LABEL = { JUBILACION: 'Jubilación', PENSION: 'Pensión' };
const ORGANISMO_RETENCION_LABEL = { ANSES: 'ANSES', CAJA_JUBILACIONES: 'Caja de Jubilaciones de Córdoba', IAF: 'I.A.F.' };

/** Panel destacado del análisis inicial (período reclamado, montos, si conviene o no). Mismo
 * estilo que PanelDestacadoReajuste, con edición inline (botón arriba a la derecha): estos datos
 * se cargan por primera vez al avanzar de estado, pero se pueden corregir después sin tener que
 * volver a pasar por la transición. */
function PanelDestacadoGanancias({ tramite, onGuardarAnalisis }) {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({});
    const [guardando, setGuardando] = useState(false);

    const abrirEdicion = () => {
        setForm({
            anioDesde: tramite.anioDesde ?? '',
            anioHasta: tramite.anioHasta ?? '',
            cantidadAnios: tramite.cantidadAnios ?? '',
            montoRetenido: tramite.montoRetenido ?? '',
            montoReclamado: tramite.montoReclamado ?? '',
        });
        setEditMode(true);
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            await onGuardarAnalisis({
                anioDesde: form.anioDesde !== '' ? form.anioDesde : null,
                anioHasta: form.anioHasta !== '' ? form.anioHasta : null,
                cantidadAnios: form.cantidadAnios !== '' ? form.cantidadAnios : null,
                montoRetenido: form.montoRetenido !== '' ? form.montoRetenido : null,
                montoReclamado: form.montoReclamado !== '' ? form.montoReclamado : null,
            });
            setEditMode(false);
        } finally {
            setGuardando(false);
        }
    };

    if (tramite.fechaAnalisis == null && tramite.montoRetenido == null && tramite.montoReclamado == null && !editMode) {
        return (
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-0">
                            <h4 className="fw-bold text-success mb-0"><i className="bi bi-calculator me-2"></i>Análisis Inicial</h4>
                            <button className="btn btn-outline-success btn-sm rounded-pill px-3 fw-semibold" onClick={abrirEdicion}>
                                <i className="bi bi-pencil me-2"></i>Cargar análisis
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (editMode) {
        return (
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-success mb-0">
                                <i className="bi bi-calculator me-2"></i>Análisis Inicial
                            </h4>
                            <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill px-3 py-2">
                                <i className="bi bi-pencil me-1"></i>Editando
                            </span>
                        </div>
                        <div className="p-4 border rounded-3 bg-light">
                            <div className="row g-3 mb-1">
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">Año desde</label>
                                    <input type="number" className="form-control shadow-sm" value={form.anioDesde}
                                        onChange={(e) => setForm(p => ({ ...p, anioDesde: e.target.value }))} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">Año hasta</label>
                                    <input type="number" className="form-control shadow-sm" value={form.anioHasta}
                                        onChange={(e) => setForm(p => ({ ...p, anioHasta: e.target.value }))} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">Cantidad de años</label>
                                    <input type="number" className="form-control shadow-sm" value={form.cantidadAnios}
                                        onChange={(e) => setForm(p => ({ ...p, cantidadAnios: e.target.value }))} />
                                </div>
                            </div>
                            <div className="row g-3 mt-1">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Monto retenido</label>
                                    <InputMonto value={form.montoRetenido} onChange={(v) => setForm(p => ({ ...p, montoRetenido: v }))} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Monto a reclamar</label>
                                    <InputMonto value={form.montoReclamado} onChange={(v) => setForm(p => ({ ...p, montoReclamado: v }))} />
                                </div>
                            </div>
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <button className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Guardar análisis</>}
                                </button>
                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={() => setEditMode(false)} disabled={guardando}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const retenido = formatMoneda(tramite.montoRetenido);
    const reclamado = formatMoneda(tramite.montoReclamado);
    const periodo = (tramite.anioDesde && tramite.anioHasta) ? `${tramite.anioDesde} — ${tramite.anioHasta}` : null;

    const tile = (titulo, valor, gradiente, icono) => (
        <div className="col-md-4">
            <div className="card shadow-sm h-100" style={{ border: 'none', borderRadius: '12px', background: gradiente }}>
                <div className="card-body" style={{ padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div style={{ color: 'white' }}>
                            <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: '0.3rem', fontWeight: 600 }}>{titulo}</div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{valor ?? '—'}</div>
                        </div>
                        <i className={`bi ${icono}`} style={{ fontSize: '2.25rem', color: 'rgba(255,255,255,0.35)' }}></i>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="row g-4 mt-1">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-success mb-0"><i className="bi bi-calculator me-2"></i>Análisis Inicial</h4>
                        <button className="btn btn-outline-success btn-sm rounded-pill px-3 fw-semibold" onClick={abrirEdicion}>
                            <i className="bi bi-pencil me-2"></i>Editar análisis
                        </button>
                    </div>
                    <div className="row g-3">
                        {tile('PERÍODO RECLAMADO', periodo, 'var(--success-solid)', 'bi-calendar-range')}
                        {tile('MONTO RETENIDO', retenido, 'var(--gradient-warning)', 'bi-cash-stack')}
                        {tile('MONTO RECLAMADO', reclamado, 'var(--gradient-purple)', 'bi-graph-up-arrow')}
                    </div>
                    {tramite.conviene === false && tramite.motivoNoConviene && (
                        <div className="alert alert-secondary border-0 rounded-3 mt-3 mb-0">
                            <strong>Motivo por el que no convino iniciar:</strong> {tramite.motivoNoConviene}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Mini-formulario por transición (patrón RTI/ART/Reajuste): cada avance de estado carga su propio
// dato de la etapa. Todos los campos son opcionales — solo agilizan cargar el dato en el momento
// del cambio; se pueden completar después editando la ficha.
const TRANSICIONES_BASE = {
    'Análisis inicial': [
        {
            destino: 'DEMANDA_PREPARACION',
            label: 'Conviene: preparar demanda',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'anioDesde', label: 'Año desde', type: 'number', icon: 'bi-calendar-event' },
                { name: 'anioHasta', label: 'Año hasta', type: 'number', icon: 'bi-calendar-event' },
                { name: 'cantidadAnios', label: 'Cantidad de años reclamados', type: 'number', icon: 'bi-hash' },
                { name: 'montoRetenido', label: 'Monto retenido', type: 'moneda' },
                { name: 'montoReclamado', label: 'Monto a reclamar', type: 'moneda' },
            ],
        },
        {
            destino: 'NO_CONVIENE',
            label: 'Cerrar: no conviene iniciar',
            icon: 'bi-x-octagon',
            variant: 'outline-secondary',
            campos: [
                { name: 'motivoNoConviene', label: 'Motivo (por qué no conviene iniciar)', type: 'textarea' },
            ],
        },
    ],
    'Demanda en preparación': [
        {
            destino: 'DEMANDA_PRESENTADA',
            label: 'Marcar demanda presentada',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'fechaDemandaPresentada', label: 'Fecha de presentación', type: 'date', icon: 'bi-calendar-check' },
                { name: 'nroExpediente', label: 'N° de expediente', type: 'text', icon: 'bi-hash' },
            ],
        },
    ],
    'Demanda presentada': [
        {
            destino: 'TRAMITE_JUDICIAL',
            label: 'Avanzar a Trámite Judicial',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'fechaInicioTramiteJudicial', label: 'Fecha de inicio del trámite judicial', type: 'date', icon: 'bi-calendar-plus' },
                { name: 'juzgado', label: 'Juzgado', type: 'text', icon: 'bi-bank' },
                { name: 'caratula', label: 'Carátula', type: 'text', icon: 'bi-file-earmark-text' },
                { name: 'nroExpedienteJudicial', label: 'N° de expediente judicial', type: 'text', icon: 'bi-hash' },
            ],
        },
    ],
    'Trámite judicial': [
        {
            destino: 'CERRADO_FAVORABLE',
            label: 'Sentencia favorable',
            icon: 'bi-check-circle-fill',
            variant: 'success',
            campos: [
                { name: 'comentario', label: 'Detalle del cierre (opcional)', type: 'textarea' },
            ],
        },
        {
            destino: 'CERRADO_DESFAVORABLE',
            label: 'Sentencia desfavorable',
            icon: 'bi-x-octagon-fill',
            variant: 'danger',
            campos: [
                { name: 'comentario', label: 'Motivo del cierre desfavorable', type: 'textarea' },
            ],
        },
    ],
};

// Cancelar / dar de baja el trámite: disponible desde CUALQUIER estado vivo, no en un punto
// puntual del ciclo (mismo patrón que Reajuste, V208). No confundir con "No conviene": ese
// cierre es un resultado del análisis, cancelado significa que el trámite no siguió.
const TRANSICION_CANCELAR = {
    destino: 'CANCELADO',
    label: 'Cancelar trámite',
    icon: 'bi-slash-circle-fill',
    variant: 'outline-secondary',
    campos: [
        { name: 'comentario', label: 'Motivo de la cancelación', type: 'textarea' },
    ],
};

const TRANSICION_REACTIVAR = {
    destino: 'ANALISIS_INICIAL',
    label: 'Reactivar trámite',
    icon: 'bi-arrow-counterclockwise',
    variant: 'outline-primary',
    campos: [
        { name: 'comentario', label: 'Motivo de la reactivación', type: 'textarea' },
    ],
};

const TRANSICIONES_POR_ESTADO = {
    ...Object.fromEntries(
        Object.entries(TRANSICIONES_BASE).map(([estado, transiciones]) => [
            estado,
            [...transiciones, TRANSICION_CANCELAR],
        ])
    ),
    'Cancelado': [TRANSICION_REACTIVAR],
};

function MiniFormularioTransicion({ idTramite, transicion, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);

    const handleChange = (name, value) => setValores(prev => ({ ...prev, [name]: value }));

    const guardar = async () => {
        setGuardando(true);
        try {
            const payload = { destino: transicion.destino, ...valores };
            const actualizado = await avanzarEstadoGanancias(idTramite, payload);
            onGuardado(actualizado);
            toast.success('Estado actualizado correctamente');
            setAbierto(false);
            setValores({});
        } catch (err) {
            console.error('Error al avanzar de estado:', err);
            toast.error('No se pudo avanzar de estado');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className={`btn btn-${transicion.variant || 'primary'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`} onClick={() => setAbierto(true)}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'}`}></i>{transicion.label}
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold text-success mb-3">
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'} me-2`}></i>{transicion.label}
            </h5>
            <div className="row g-3">
                {transicion.campos.map(campo => (
                    <div key={campo.name} className={campo.type === 'textarea' ? 'col-12' : 'col-md-6'}>
                        <label className="form-label fw-semibold">{campo.label}</label>
                        {campo.type === 'textarea' ? (
                            <textarea className="form-control shadow-sm" rows={3}
                                value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                        ) : campo.type === 'moneda' ? (
                            <InputMonto value={valores[campo.name] ?? ''} onChange={(v) => handleChange(campo.name, v)} />
                        ) : (
                            <div className="input-group">
                                {campo.icon && <span className="input-group-text bg-light"><i className={`bi ${campo.icon}`}></i></span>}
                                <input type={campo.type} className="form-control shadow-sm"
                                    value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Confirmar</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => { setAbierto(false); setValores({}); }} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

/** Reemplaza el selector libre del historial como forma primaria de cambiar de estado (patrón
 * RTI/ART/Reajuste). El historial completo sigue disponible desde el botón "Ver historial". */
function SeccionAvanzarEstado({ idTramite, nombreEstadoActual, onAvanzado }) {
    const transiciones = TRANSICIONES_POR_ESTADO[nombreEstadoActual] || [];
    if (transiciones.length === 0) {
        return (
            <div className="alert alert-secondary border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                <i className="bi bi-flag-fill"></i>Este trámite está en una etapa final del ciclo de estados.
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-3">
            {transiciones.map(t => (
                <MiniFormularioTransicion key={t.destino} idTramite={idTramite} transicion={t} onGuardado={onAvanzado} />
            ))}
        </div>
    );
}

function VistaDetalladaTramiteGanancias() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edición de datos del trámite (no el cliente)
    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    // Herramienta: crear turno prellenado con los datos del cliente del trámite (firma documental)
    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    // Archivos del trámite (fuente única: el checklist enlaza por idArchivo; "otros" son los no vinculados)
    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteGananciasById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Ganancias:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteGananciasById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM/Reajuste)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const formatLocalFecha = (fechaInput) => {
        if (!fechaInput) return '';
        if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
        const d = new Date(fechaInput);
        if (Number.isNaN(d.getTime())) return '';
        return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-ganancias' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const formatearFechaEvento = (fechaString) => {
        if (!fechaString) return '';
        const [y, m, d] = fechaString.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    // ---- Descarga con nombre real (extensión + tipo correctos) ----
    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    // ---- Checklist ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return; // con archivo adjunto, el ítem queda marcado y bloqueado
        try {
            await actualizarChecklistItem(item.id, { presentado: !item.presentado, idArchivo: null });
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivo = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistItem(item.id, { presentado: true, idArchivo });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjunto = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistItem(item.id, { presentado: false, idArchivo: null });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    // ---- Otros documentos (no vinculados al checklist) ----
    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Comentarios (nota editable con firma, igual que el resto de los módulos) ----
    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosGanancias(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    // ---- Editar datos del trámite ----
    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            beneficio: tramite.beneficio || 'JUBILACION',
            organismoRetencion: tramite.organismoRetencion || 'ANSES',
            fechaJubilacion: tramite.fechaJubilacion || '',
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async () => {
        setGuardandoDatos(true);
        try {
            await actualizarTramiteGanancias(idTramite, {
                ...tramiteEdit,
                organismo: tramiteEdit.organismo || null,
                fechaJubilacion: tramiteEdit.fechaJubilacion || null,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            console.error('Error al guardar datos:', error);
            toast.error(error.response?.data || 'No se pudieron guardar los datos');
        } finally {
            setGuardandoDatos(false);
        }
    };

    // ---- Avanzar de estado (mini-formulario por transición, patrón RTI/ART/Reajuste) ----
    const handleAvanzarEstado = (tramiteActualizado) => {
        setTramite(tramiteActualizado);
    };

    // ---- Editar el análisis inicial (independiente del cambio de estado, botón propio en el panel) ----
    const handleGuardarAnalisis = async (valores) => {
        try {
            const actualizado = await actualizarAnalisisGanancias(idTramite, valores);
            setTramite(actualizado);
            toast.success('Análisis actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar el análisis:', error);
            toast.error('No se pudo guardar el análisis');
            throw error;
        }
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-success" style={{ width: '3rem', height: '3rem' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const tieneHitos = [
        tramite.fechaDemandaPresentada, tramite.nroExpediente, tramite.fechaInicioTramiteJudicial,
        tramite.juzgado, tramite.caratula, tramite.nroExpedienteJudicial,
    ].some((v) => v != null && v !== '');
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold text-success mb-0">Detalles del Trámite de Impuesto a las Ganancias</h2>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                            GANANCIAS
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <BotonGenerarCaratula
                        tipo="GANANCIAS"
                        idTramite={tramite.idTramite}
                        onGenerado={recargarArchivos}
                    />
                    <button
                        className="btn btn-outline-success d-flex align-items-center gap-2 rounded-pill"
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            <BannerEstadoFinal
                idTramite={tramite.idTramite}
                nombreEstadoActual={tramite.nombreEstadoActual}
            />

            {/* Estado del ciclo: cada avance dispara el mini-formulario de esa etapa */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                            {tramite.nombreEstadoActual || 'Análisis inicial'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setModalHistorialAbierto(true)}>
                        <i className="bi bi-clock-history me-1"></i>Ver historial
                    </button>
                </div>
                <SeccionAvanzarEstado idTramite={idTramite} nombreEstadoActual={tramite.nombreEstadoActual} onAvanzado={handleAvanzarEstado} />
            </div>

            {/* Nota editable de comentarios (con firma, igual que el resto de los módulos) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Ganancias"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-success mb-0">Eventos del Trámite</h4>
                            <button
                                className="btn btn-outline-success d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-success" role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold text-success mb-0">{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge bg-success rounded-pill">{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2">{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="text-success fw-semibold">Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Datos del Beneficio + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold text-success mb-3">
                            <i className="bi bi-file-earmark-text me-2"></i>
                            Datos del Beneficio
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Beneficio:</strong> {BENEFICIO_LABEL[tramite.beneficio] || tramite.beneficio}</p>
                                <p><strong>Organismo de retención:</strong> {ORGANISMO_RETENCION_LABEL[tramite.organismoRetencion] || tramite.organismoRetencion}</p>
                                <p><strong>Fecha de jubilación:</strong> {tramite.fechaJubilacion || 'N/A'}</p>
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-success rounded-pill px-4 fw-semibold" onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="row g-2 mb-2">
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Beneficio</label>
                                        <select className="form-select" value={tramiteEdit.beneficio}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, beneficio: e.target.value }))}>
                                            <option value="JUBILACION">Jubilación</option>
                                            <option value="PENSION">Pensión</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Organismo de retención</label>
                                        <select className="form-select" value={tramiteEdit.organismoRetencion}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, organismoRetencion: e.target.value }))}>
                                            <option value="ANSES">ANSES</option>
                                            <option value="CAJA_JUBILACIONES">Caja de Jubilaciones de Córdoba</option>
                                            <option value="IAF">I.A.F.</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Fecha de jubilación</label>
                                    <input type="date" className="form-control" value={tramiteEdit.fechaJubilacion || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, fechaJubilacion: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-success rounded-pill px-4 fw-semibold" onClick={handleGuardarDatos} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Panel destacado del análisis inicial — arriba de Documentación */}
            <PanelDestacadoGanancias tramite={tramite} onGuardarAnalisis={handleGuardarAnalisis} />

            {/* Seguimiento del trámite: los datos que se van cargando en cada avance de estado
                (demanda presentada, trámite judicial), debajo del análisis inicial. */}
            {tieneHitos && (
                <div className="row g-4 mt-1">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 rounded-4 p-4">
                            <h4 className="fw-bold text-success mb-3"><i className="bi bi-signpost-split me-2"></i>Seguimiento del Trámite</h4>
                            <div className="row g-2">
                                {tramite.fechaDemandaPresentada && <div className="col-md-6"><strong>Fecha de demanda presentada:</strong> {tramite.fechaDemandaPresentada}</div>}
                                {tramite.nroExpediente && <div className="col-md-6"><strong>N° de expediente:</strong> {tramite.nroExpediente}</div>}
                                {tramite.fechaInicioTramiteJudicial && <div className="col-md-6"><strong>Inicio del trámite judicial:</strong> {tramite.fechaInicioTramiteJudicial}</div>}
                                {tramite.juzgado && <div className="col-md-6"><strong>Juzgado:</strong> {tramite.juzgado}</div>}
                                {tramite.caratula && <div className="col-md-6"><strong>Carátula:</strong> {tramite.caratula}</div>}
                                {tramite.nroExpedienteJudicial && <div className="col-md-6"><strong>N° de expediente judicial:</strong> {tramite.nroExpedienteJudicial}</div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Documentación del Trámite: checklist de formularios + otros documentos (un solo lugar) */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold text-success mb-3"><i className="bi bi-folder2-open me-2"></i>Documentación del Trámite</h4>

                        {/* Formularios requeridos */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-check2-square me-2"></i>Formularios requeridos</h6>
                            <span className={`badge ${claseBadgeContador(formularios.filter(f => f.presentado).length, formularios.length)}`}>{formularios.filter(f => f.presentado).length}/{formularios.length}</span>
                        </div>
                        {formularios.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                                <p className="mb-0">No hay formularios en el checklist.</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)}
                                                        title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>
                                                        {nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                        onClick={() => quitarAdjunto(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id
                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => adjuntarArchivo(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <hr className="my-4" />

                        {/* Otros documentos (no vinculados a un formulario) */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-paperclip me-2"></i>Otros documentos</h6>
                            <label className="btn btn-sm btn-outline-primary rounded-pill mb-0">
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay otros documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text text-success" style={{ fontSize: '1.25rem' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            {/* Acciones irreversibles (cambiar tipo / eliminar definitivamente).
                El propio panel decide si mostrarse según el rol. */}
            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="GANANCIAS"
            />

            {/* Modal historial de estados (ámbito GANANCIAS) */}
            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="GANANCIAS"
            />

            {/* Modal crear turno (reutiliza el turnero de Recepción, prellenado con el cliente).
                Único turno posible en este trámite: firmar documental (ej. escribanía). */}
            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarModalTurno(false)}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            celdaSel={null}
                            valoresIniciales={{
                                nombre: cliente.nombre || '',
                                apellido: cliente.apellido || '',
                                cuil: cliente.cuil || '',
                                telefono: cliente.telefonoPropio || '',
                                tipoTramite: 'GANANCIAS',
                            }}
                            onCerrar={() => setMostrarModalTurno(false)}
                            onCreado={() => { setMostrarModalTurno(false); toast.success('Turno creado correctamente'); }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteGanancias;
